"use client";

import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
// import { Badge } from "./ui/badge";
import { useNotifications } from "./NotificationManager";
import { 
  Bell, 
  BellRing,
  // Check,
  CheckCheck,
  Clock,
  Mail,
  X,
  AlertCircle,
  Info
} from "lucide-react";

interface NotificationBellProps {
  userId: string;
  userType: 'candidate' | 'hr';
  className?: string;
}

export function NotificationBell({ userId, userType, className = "" }: NotificationBellProps) {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications(userId, userType);
  const [isOpen, setIsOpen] = React.useState(false);
  const [hasNewNotifications, setHasNewNotifications] = React.useState(false);

  // Animate bell when new notifications arrive
  React.useEffect(() => {
    if (unreadCount > 0) {
      setHasNewNotifications(true);
      const timer = setTimeout(() => setHasNewNotifications(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [unreadCount]);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'candidate_incomplete':
        return <Clock className="w-4 h-4 text-amber-600" />;
      case 'candidate_welcome_back':
        return <Info className="w-4 h-4 text-blue-600" />;
      case 'hr_review_pending':
      case 'hr_urgent_review':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Mail className="w-4 h-4 text-gray-600" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'border-l-red-500 bg-red-50';
      case 'medium':
        return 'border-l-amber-500 bg-amber-50';
      case 'low':
        return 'border-l-blue-500 bg-blue-50';
      default:
        return 'border-l-gray-500 bg-gray-50';
    }
  };

  const getTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <div className={`relative ${className}`}>
      {/* Bell Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 hover:bg-gray-100 rounded-full"
      >
        <motion.div
          animate={hasNewNotifications ? {
            rotate: [0, -15, 15, -15, 0],
            scale: [1, 1.1, 1]
          } : {}}
          transition={{ duration: 0.5 }}
        >
          {unreadCount > 0 ? (
            <BellRing className="w-5 h-5 text-gray-700" />
          ) : (
            <Bell className="w-5 h-5 text-gray-700" />
          )}
        </motion.div>

        {/* Unread Count Badge */}
        {unreadCount > 0 && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </motion.div>
        )}
      </Button>

      {/* Notification Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />

            {/* Dropdown */}
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              className="absolute right-0 top-full mt-2 w-80 z-50"
            >
              <Card className="bg-white shadow-xl border border-gray-200 rounded-xl overflow-hidden">
                {/* Header */}
                <div className="p-4 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <h3 className="text-gray-900">Notifications</h3>
                    <div className="flex items-center space-x-2">
                      {unreadCount > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={markAllAsRead}
                          className="text-xs text-blue-600 hover:text-blue-700"
                        >
                          <CheckCheck className="w-3 h-3 mr-1" />
                          Mark all read
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsOpen(false)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Notifications List */}
                <div className="max-h-96 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="p-6 text-center text-gray-500">
                      <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p>No notifications yet</p>
                      <p className="text-xs text-gray-400 mt-1">
                        You're all caught up!
                      </p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {notifications.map((notification, index) => (
                        <motion.div
                          key={notification.id}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors border-l-4 ${
                            getPriorityColor(notification.priority)
                          } ${!notification.readAt ? 'bg-blue-50/50' : ''}`}
                          onClick={() => markAsRead(notification.id)}
                        >
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0 mt-0.5">
                              {getNotificationIcon(notification.type)}
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between">
                                <p className="text-gray-900 text-sm">
                                  {notification.title}
                                </p>
                                <div className="flex items-center space-x-2 ml-2">
                                  {!notification.readAt && (
                                    <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                                  )}
                                  <span className="text-xs text-gray-500 whitespace-nowrap">
                                    {getTimeAgo(notification.createdAt)}
                                  </span>
                                </div>
                              </div>
                              
                              <p className="text-gray-600 text-xs mt-1 leading-relaxed">
                                {notification.message}
                              </p>

                              {/* Additional info for specific notification types */}
                              {notification.type === 'hr_review_pending' && notification.data && (
                                <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                                  <span>Candidate: {notification.data.candidateName}</span>
                                  <span className="capitalize">Role: {notification.data.joinerType}</span>
                                </div>
                              )}

                              {notification.sentAt && (
                                <div className="mt-2 flex items-center space-x-1 text-xs text-gray-400">
                                  <Mail className="w-3 h-3" />
                                  <span>Email sent</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Footer */}
                {notifications.length > 0 && (
                  <div className="p-3 border-t border-gray-200 bg-gray-50 text-center">
                    <span className="text-xs text-gray-500">
                      {notifications.length} notification{notifications.length !== 1 ? 's' : ''} total
                    </span>
                  </div>
                )}
              </Card>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}